import React from 'react';
import {
  Tool,
  ToolHeader,
  ToolContent,
} from '@cotera/client/v0/components/ai-elements/tool';
import { Text } from '@cotera/client/app/components/ui';
import {
  agentActionId,
  feActionIdFromToolKey,
  useActionsContext,
  type Action,
} from '@cotera/client/v0/actions/framework';
import type { ToolRendererProps } from '@cotera/client/v0/chat/tool-renderers';
import { useAnswerInterruptedTool } from './use-answer-interrupted-tool';

/**
 * How much of an action's output is worth sending back. It goes verbatim into
 * the model's context — this path does not get the truncation normal tool
 * responses do — so an action returning a whole grid would blow the window.
 */
const MAX_OUTPUT_CHARS = 8_000;

/**
 * Renders a `client/fe-action/*` call, and runs it.
 *
 * The action lives in this browser, so calling it suspends the run and this is
 * what completes it. Nothing is asked of the user first: opting an action into
 * the chat scope is the consent, and the call arrives with a full payload.
 *
 * Matched by namespace in the v1 renderer resolver rather than registered per
 * key, because which actions exist is only known at runtime.
 */
export const FrontendActionRenderer: React.FC<ToolRendererProps> = ({
  part,
  chatModel,
  readonly,
}) => {
  const context = useActionsContext();
  const toolKey = part.toolKey().toString();
  const actionId = feActionIdFromToolKey(toolKey) ?? toolKey;

  const action = context
    .getAllActions()
    .find((candidate: Action) => agentActionId(candidate) === actionId);

  const { awaiting, failure } = useAnswerInterruptedTool({
    part,
    chatModel,
    readonly,
    answer: async () => {
      // What was discoverable when Coco listed the page may be gone by the time
      // it calls: the user moves around while it thinks. Saying so beats leaving
      // the call open forever.
      if (action === undefined) {
        return {
          error: `"${actionId}" is not available. The user has navigated away from the page that provided it.`,
        };
      }
      if (!action.applicable(context)) {
        return {
          error: `"${actionId}" does not apply to what the user is looking at now.`,
        };
      }

      // Actions trust their payload — `askOrExecute` casts rather than parses —
      // so the model's arguments are checked here before anything runs.
      const parsed = action.inputSchema.safeParse(part.input ?? {});
      if (!parsed.success) {
        return {
          error: 'Invalid input for this action.',
          issues: parsed.error.issues.map((issue) => ({
            path: issue.path.join('.'),
            message: issue.message,
          })),
        };
      }

      // `askOrExecute` rather than `execute`: the payload is complete and a
      // chat-scoped action must not dialog, so this runs straight through. It is
      // also the only entry point on the `Action` contract.
      const result = await action.askOrExecute(parsed.data, context);
      if (result.isErr()) {
        return { error: 'The action failed.', errors: result.error };
      }
      // Cancellation resolves as success, so it has to be read off the payload —
      // otherwise the model believes something ran that never did.
      if (result.value.cancelled === true) {
        return { cancelled: true, message: 'The user cancelled.' };
      }
      return clampOutput(result.value.output);
    },
  });

  const busy = part.isIncomplete() && readonly !== true;

  return (
    <Tool>
      <ToolHeader
        type={part.toolPart.type}
        title={action?.title ?? part.toolKey().displayName()}
        state={busy ? 'input-available' : 'output-available'}
      />
      <ToolContent>
        <div className="px-4 py-2">
          {failure !== null ? (
            <Text.P className="text-destructive">{failure}</Text.P>
          ) : busy || awaiting ? (
            <Text.P className="text-muted-foreground">Running…</Text.P>
          ) : (
            <pre className="overflow-x-auto text-xs">
              {JSON.stringify(part.getOutput() ?? {}, null, 2)}
            </pre>
          )}
        </div>
      </ToolContent>
    </Tool>
  );
};

/** Normalise an action's output to a bounded object the model can read. */
function clampOutput(output: unknown): Record<string, unknown> {
  if (output === undefined || output === null) {
    return { ok: true };
  }
  const wrapped =
    typeof output === 'object' && !Array.isArray(output)
      ? (output as Record<string, unknown>)
      : { result: output };

  const serialized = JSON.stringify(wrapped) ?? '';
  if (serialized.length <= MAX_OUTPUT_CHARS) {
    return wrapped;
  }
  return {
    truncated: true,
    note: `Output was ${serialized.length} characters and has been truncated. Ask for a narrower result if you need more.`,
    preview: serialized.slice(0, MAX_OUTPUT_CHARS),
  };
}
